import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  useTheme,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  TextField,
  alpha
} from '@mui/material';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { userApi } from 'api';
import { useSocketContext } from 'contexts/SocketContext';
import { personalDataStore } from 'stores/personalDataStore';
import { useParams, useNavigate } from 'react-router-dom';
import { interviewStore } from 'stores/interviewStore';
import { skillsStore } from 'stores/skillsStore';
import { sortBy } from 'lodash';
import LoadingScreen from 'components/LoadingScreen';

const InterviewPaymentPage = () => {
  const { formId } = useParams<{ formId: string }>();
  const [price, setPrice] = useState('');
  const [fullPrice, setFullPrice] = useState('');
  const navigate = useNavigate();
  const { paymentMessages: messages } = useSocketContext();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoApplied, setPromoApplied] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false)

  const formIsAvailable = useMemo(
    () => !personalDataStore.payments.find((i) => i.formId === formId),
    [formId]
  );

  // Пришли со страницы результатов (пейволл отчёта): после оплаты возвращаем
  // пользователя обратно на /results/:id вместо запуска нового интервью.
  const backToResultInterviewId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('backTo') === 'result' ? params.get('interviewId') : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setIsDataLoading(true)
        await Promise.all([
          personalDataStore.getPayments(),
          personalDataStore.getExtractInterviews(),
          personalDataStore.getDiscounts()
        ])
      } catch (e) {
        setError('Ошибка загрузки данных для платежной формы')
      } finally {
        setIsDataLoading(true)
      }
    };

    load();
  }, []);

  const extractInterview = useMemo(() => {
    // return personalDataStore.extractInterviews.filter(i => !i.vacancyId).find(i => i.speciality === personalDataStore.user?.speciality && i.level === personalDataStore.user?.specialityLevel && i.position === personalDataStore.user?.position);
    const filtered = personalDataStore.extractInterviews.filter((i) => !i.vacancyId);
    const user = personalDataStore.user;

    const sorted = sortBy(
      filtered.filter(
        (i) =>
          i.speciality === user?.speciality &&
          i.level === user?.specialityLevel.toLowerCase() &&
          i.name === user?.position
      ),
      'createdAt',
      'asc'
    );

    if (sorted.length > 0) {
      return sorted[0];
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDataStore.extractInterviews]);

  const startInterview = useCallback(async () => {
    try {
      if (!personalDataStore.user) return;
      setIsSubmitted(true);
      await personalDataStore.getPayments();
      await personalDataStore.getExtractInterviews();
      if (backToResultInterviewId) {
        navigate(`/results/${backToResultInterviewId}`);
        return;
      }
      if (location.search.includes('backTo=profile')) {
        navigate('/profile');
        return;
      }
      if (!extractInterview) {
        await skillsStore.extractCompetences({
          position: personalDataStore.user.position,
          speciality: personalDataStore.user.speciality,
          level: personalDataStore.user.specialityLevel
        });

        navigate(`/skill-lvl/${skillsStore.interviewId}`);
        return;
      }

      await interviewStore.startInterview(extractInterview.interviewId as string);
      // if (location.search.includes('backTo=profile')) {
      //   navigate('/profile');
      //   return;
      // }
      if (interviewStore.currentInterview?.id) {
        navigate(`/video-interview/record/${interviewStore.currentInterview.id}`);
      } else {
        setError('Не удалось начать интервью!');
        return;
      }
    } catch (e) {
      setError(e as any);
    } finally {
      setIsSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, backToResultInterviewId]);

  useEffect(() => {
    userApi
      .getPrice()
      .then((price) => {
        setPrice(price.finalPrice);
        setFullPrice(price.fullPrice);
      })
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    if (messages.find((i) => i.formId === formId)) {
      setPaymentOpen(false);
      startInterview();
    }
  }, [messages, formId, startInterview]);

  // Ссылку на оплату формирует бэкенд (подпись/Receipt/сумма — на сервере).
  const handlePayment = async () => {
    if (!formId) return;
    setPaymentUrl(null);
    try {
      if(discountPercent === 100) {
        await userApi.createFreePayment({ formId });
      } else {
        setPaymentOpen(true);
        setWidgetLoading(true);
        const url = await userApi.getRobokassaUrl({ formId, paymentType: 'interview' });
        setPaymentUrl(url);
      }
    } catch (e) {
      console.error(e);
      setError('Не удалось открыть форму оплаты. Попробуйте ещё раз.');
      setPaymentOpen(false);
      setWidgetLoading(false);
    }
  };

  const handleClosePayment = () => {
    setPaymentOpen(false);
    setPaymentUrl(null);
  };

  const handleIframeLoad = () => {
    setWidgetLoading(false);
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      await userApi.applyPromoCode({ code });
      // После успешного ответа (200) перезапрашиваем актуальную цену со скидкой.
      const updatedPrice = await userApi.getPrice();
      setPrice(updatedPrice.finalPrice);
      setFullPrice(updatedPrice.fullPrice);
      setPromoApplied(true);
    } catch (e) {
      console.error(e);
      setPromoError('Не удалось применить промокод. Проверьте код и попробуйте ещё раз.');
    } finally {
      setPromoLoading(false);
    }
  };

  const theme = useTheme();

  // Скидка: показываем старую цену и бейдж, только если fullPrice отличается от finalPrice.
  const hasDiscount =
    !!fullPrice && !!price && fullPrice !== price && parseFloat(fullPrice) > parseFloat(price);
  const discountPercent = hasDiscount
    ? Math.round((1 - parseFloat(price) / parseFloat(fullPrice)) * 100)
    : 0;

  if (isSubmitted) {
    return <LoadingScreen label="Создаем интервью..." />;
  }

  return (
    <Stack
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 520,
          width: '100%',
          boxShadow: theme.shadows[10],
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            {/* Icon */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
            >
              <Icon icon="mdi:credit-card-check-outline" width={36} height={36} />
            </Box>

            {/* Title */}
            <Typography variant="h5" fontWeight={700}>
              {backToResultInterviewId ? 'Доступ к полному отчёту' : 'Доступ к интервью'}
            </Typography>

            {/* Description */}
            <Typography variant="body1" color="text.secondary">
              {backToResultInterviewId
                ? 'Интервью пройдено! Чтобы открыть полный аналитический отчёт по вашим ответам, необходимо внести разовую оплату.'
                : 'Для прохождения интервью и получения подробного аналитического отчёта необходимо внести разовую оплату.'}
            </Typography>

            {/* Benefits */}
            <Stack spacing={1} sx={{ textAlign: 'left', width: '100%', pl: 4 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon icon="mdi:check-circle-outline" color={theme.palette.success.main} />
                <Typography variant="body2">Оценка по каждому профессиональному навыку</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon icon="mdi:check-circle-outline" color={theme.palette.success.main} />
                <Typography variant="body2">
                  Детальный отчёт с сильными и слабыми сторонами
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon icon="mdi:check-circle-outline" color={theme.palette.success.main} />
                <Typography variant="body2">Персональные рекомендации для роста</Typography>
              </Stack>
            </Stack>

            {/* Price */}
            <Box sx={{ pt: 2 }}>
              {hasDiscount && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <Typography
                    variant="h6"
                    color="text.disabled"
                    sx={{ textDecoration: 'line-through', fontWeight: 500 }}
                  >
                    {fullPrice} ₽
                  </Typography>
                  {discountPercent > 0 && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: alpha(theme.palette.error.main, 0.12),
                        color: 'error.main'
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        −{discountPercent}%
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
              <Typography variant="h4" fontWeight={700}>
                {price} ₽
              </Typography>
              <Typography variant="caption" color="text.secondary">
                разовый платёж
              </Typography>
            </Box>

            {/* Промокод */}
            <Box sx={{ width: '100%' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  label="Промокод"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoError(null);
                  }}
                  disabled={promoLoading || promoApplied}
                  error={!!promoError}
                />
                <Button
                  variant="outlined"
                  onClick={handleApplyPromo}
                  disabled={!promoCode.trim() || promoLoading || promoApplied}
                  sx={{ minWidth: 120, height: 40, whiteSpace: 'nowrap' }}
                >
                  {promoLoading ? <CircularProgress size={20} /> : 'Применить'}
                </Button>
              </Stack>
              {promoError && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5, textAlign: 'left' }}
                >
                  {promoError}
                </Typography>
              )}
              {promoApplied && (
                <Typography
                  color="success.main"
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5, textAlign: 'left' }}
                >
                  Промокод применён — цена обновлена.
                </Typography>
              )}
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
            {!formIsAvailable && (
              <Typography color="error" variant="body1">
                Данная форма оплаты недействительна!
              </Typography>
            )}
            {/* Pay button */}
            {formIsAvailable ? (
              <Button
                size="large"
                variant="contained"
                fullWidth
                disabled={isSubmitted}
                onClick={handlePayment}
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontWeight: 600
                }}
                startIcon={<Icon icon="mdi:lock-check-outline" />}
              >
                {backToResultInterviewId ? 'Оплатить и открыть отчёт' : 'Оплатить и пройти интервью'}
              </Button>
            ) : (
              <Button
                size="large"
                variant="contained"
                color="secondary"
                fullWidth
                disabled={!price || isSubmitted}
                onClick={() => navigate('/main')}
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontWeight: 600
                }}
              >
                Вернуться на главынй экран
              </Button>
            )}

            {/* Footer note */}
            <Typography variant="caption" color="text.secondary">
              Оплата производится через защищённый платёжный сервис
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={paymentOpen}
        onClose={handleClosePayment}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: { xs: '100%', sm: '90vh' },
            maxHeight: { xs: '100%', sm: '90vh' },
            width: { xs: '100%', sm: '90vw' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 3 }
          }
        }}
      >
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
          <IconButton onClick={handleClosePayment} aria-label="Закрыть">
            <Icon icon="mdi:close" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
          {widgetLoading && (
            <Stack
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                backgroundColor: 'background.paper'
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Загрузка формы оплаты…
              </Typography>
            </Stack>
          )}

          {paymentUrl && (
            <Box
              component="iframe"
              src={paymentUrl}
              title="Robokassa"
              onLoad={handleIframeLoad}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default observer(InterviewPaymentPage);
